import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { config } from '../../../config/config';
import { useToast } from '../../shared/hooks/use-toast';
import { getErrorMessage } from '../../shared/utils/convert-error';
import { useClaimBonusMutation } from '../graphql/claim.graphql';

export function useClaimBonus({
    onClaimed,
    userProfileId,
}: {
    onClaimed: () => void;
    userProfileId: string;
}) {
    const intl = useIntl();
    const toast = useToast();
    const [{ fetching: isClaiming }, claim] = useClaimBonusMutation();
    const claimBonus = useCallback(
        async (id: string) => {
            try {
                const { error } = await claim({
                    xTenantID: config.TENANT_ID,
                    bonusId: id,
                    userProfileId,
                });
                onClaimed();

                if (error) {
                    toast.error(getErrorMessage(intl, 'bonuses.claim-bonus', error));
                }

                toast.success(
                    intl.formatMessage({
                        id: 'bonuses.claim-bonus.success',
                        defaultMessage: 'You successfully claimed the bonus!',
                    }),
                );
            } catch (e) {
                toast.error(getErrorMessage(intl, 'bonuses.claim-bonus', e));
            }
        },
        [userProfileId, claim, onClaimed, toast, intl],
    );

    return useMemo(() => {
        return {
            claimBonus,
            isClaiming,
        };
    }, [claimBonus, isClaiming]);
}
